/**
 * InviteUserDialog Component
 *
 * A modal dialog for inviting new users to the organization.
 * Collects email address and role, validates input, and submits the invitation.
 * Displays appropriate error messages for different failure scenarios.
 *
 * @module components/InviteUserDialog
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Box,
} from '@mui/material';
import type { UserRole } from '../types';
import {
  useUsersStore,
  isInviteUserError,
  CANNOT_MANAGE_MEMBERS_MESSAGE,
} from '../stores/useUsersStore';
import { isValidEmail } from '../utils/email';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for the InviteUserDialog component.
 */
export interface InviteUserDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback when dialog should close (cancel or successful submit) */
  onClose: () => void;

  /** Optional callback after successful invite (for parent to show snackbar) */
  onSuccess?: () => void;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Error messages for specific error types.
 */
const ERROR_MESSAGES = {
  SEAT_LIMIT: 'Seat limit reached — contact ModernPath',
  DUPLICATE_EMAIL: 'User already exists in this organization',
  GENERIC: 'Something went wrong. Please try again.',
} as const;

/**
 * Default role for new users.
 */
const DEFAULT_ROLE: UserRole = 'member';

// =============================================================================
// InviteUserDialog Component
// =============================================================================

/**
 * InviteUserDialog - Modal dialog for inviting new users.
 *
 * Features:
 * - Email input with format validation
 * - Role selection (Member/Admin) with Member as default
 * - Loading state during submission
 * - Error display for specific error types (seat limit, duplicate email)
 * - Form reset on open/close
 *
 * @example
 * ```tsx
 * function UsersPage() {
 *   const [dialogOpen, setDialogOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setDialogOpen(true)}>Invite User</Button>
 *       <InviteUserDialog
 *         open={dialogOpen}
 *         onClose={() => setDialogOpen(false)}
 *         onSuccess={() => showSnackbar('Invitation sent!')}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function InviteUserDialog({
  open,
  onClose,
  onSuccess,
}: InviteUserDialogProps) {
  // Form state
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>(DEFAULT_ROLE);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // Track previous open value to detect open transitions and reset form state.
  // This is the React-recommended pattern for adjusting state in response to
  // prop changes, instead of using useEffect with setState.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      // Reset form when dialog reopens
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole(DEFAULT_ROLE);
      setError(null);
      setIsSubmitting(false);
      setEmailTouched(false);
    }
  }

  // Store action
  const inviteUser = useUsersStore((state) => state.inviteUser);

  // Proactive per-org capability (authority + entitlement) for the viewed org.
  // Tri-state: true = can manage; false = resolved denial (gate the form with an
  // info message + disabled submit); null = unresolved. The dialog normally only
  // opens when this is true, but it's re-checked here as defense in depth — and
  // the notice shows only on an explicit false, never on null.
  const canManageTargetOrg = useUsersStore((state) => state.canManageTargetOrg);

  /**
   * Computed validation state.
   */
  const emailIsEmpty = !email.trim();
  const emailIsValid = isValidEmail(email);
  const showEmailError = emailTouched && !emailIsEmpty && !emailIsValid;
  const firstNameIsEmpty = !firstName.trim();
  const lastNameIsEmpty = !lastName.trim();
  const canSubmit =
    !emailIsEmpty &&
    emailIsValid &&
    !firstNameIsEmpty &&
    !lastNameIsEmpty &&
    !isSubmitting &&
    canManageTargetOrg === true;

  /**
   * Handle email input change.
   */
  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      // Clear error when user starts typing
      if (error) {
        setError(null);
      }
    },
    [error],
  );

  /**
   * Handle email input blur (mark as touched).
   */
  const handleEmailBlur = useCallback(() => {
    setEmailTouched(true);
  }, []);

  /**
   * Handle first-name input change.
   */
  const handleFirstNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFirstName(event.target.value);
      if (error) {
        setError(null);
      }
    },
    [error],
  );

  /**
   * Handle last-name input change.
   */
  const handleLastNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLastName(event.target.value);
      if (error) {
        setError(null);
      }
    },
    [error],
  );

  /**
   * Handle role selection change.
   */
  const handleRoleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRole(event.target.value as UserRole);
    },
    [],
  );

  /**
   * Handle cancel button click.
   */
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  /**
   * Handle form submission.
   */
  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await inviteUser(email.trim(), firstName.trim(), lastName.trim(), role);
      // Success - close dialog and notify parent
      onClose();
      onSuccess?.();
    } catch (err) {
      // Org-not-entitled is not a failure to apologize for — the store pins
      // canManageTargetOrg=false, which surfaces the informational notice and
      // disables the form. Don't render it as a red error.
      if (isInviteUserError(err) && err.errorType === 'ORG_NOT_ENTITLED') {
        setIsSubmitting(false);
        return;
      }

      // Determine error message based on error type
      let errorMessage: string = ERROR_MESSAGES.GENERIC;

      if (isInviteUserError(err)) {
        switch (err.errorType) {
          case 'SEAT_LIMIT':
            errorMessage = ERROR_MESSAGES.SEAT_LIMIT;
            break;
          case 'DUPLICATE_EMAIL':
            errorMessage = ERROR_MESSAGES.DUPLICATE_EMAIL;
            break;
          default:
            errorMessage = ERROR_MESSAGES.GENERIC;
        }
      }

      setError(errorMessage);
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    email,
    firstName,
    lastName,
    role,
    inviteUser,
    onClose,
    onSuccess,
  ]);

  /**
   * Handle Enter key press for form submission.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && canSubmit) {
        handleSubmit();
      }
    },
    [canSubmit, handleSubmit],
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      data-testid="invite-user-dialog"
    >
      <DialogTitle data-testid="invite-user-dialog-title">
        Invite User
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
          onKeyDown={handleKeyDown}
        >
          {/* Capability notice (informational) takes precedence over errors: the
              caller can't manage members in this org, so the form is gated. Shown
              only on a resolved denial (=== false), never on the unresolved null. */}
          {canManageTargetOrg === false ? (
            <Alert severity="info" data-testid="invite-user-not-entitled">
              {CANNOT_MANAGE_MEMBERS_MESSAGE}
            </Alert>
          ) : (
            error && (
              <Alert severity="error" data-testid="invite-user-error">
                {error}
              </Alert>
            )
          )}

          {/* Email Input */}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={isSubmitting}
            error={showEmailError}
            helperText={
              showEmailError ? 'Please enter a valid email address' : ''
            }
            autoFocus
            fullWidth
            required
            data-testid="invite-user-email-field"
            slotProps={{
              htmlInput: {
                'data-testid': 'invite-user-email-input',
              },
            }}
          />

          {/* First Name Input */}
          <TextField
            label="First name"
            value={firstName}
            onChange={handleFirstNameChange}
            disabled={isSubmitting}
            fullWidth
            required
            data-testid="invite-user-first-name-field"
            slotProps={{
              htmlInput: {
                maxLength: 200,
                'data-testid': 'invite-user-first-name-input',
              },
            }}
          />

          {/* Last Name Input */}
          <TextField
            label="Last name"
            value={lastName}
            onChange={handleLastNameChange}
            disabled={isSubmitting}
            fullWidth
            required
            data-testid="invite-user-last-name-field"
            slotProps={{
              htmlInput: {
                maxLength: 200,
                'data-testid': 'invite-user-last-name-input',
              },
            }}
          />

          {/* Role Selector */}
          <FormControl disabled={isSubmitting}>
            <FormLabel id="role-selector-label">Role</FormLabel>
            <RadioGroup
              aria-labelledby="role-selector-label"
              name="role"
              value={role}
              onChange={handleRoleChange}
              data-testid="invite-user-role-selector"
            >
              <FormControlLabel
                value="member"
                control={<Radio />}
                label="Member"
                data-testid="invite-user-role-member"
              />
              <FormControlLabel
                value="admin"
                control={<Radio />}
                label="Admin"
                data-testid="invite-user-role-admin"
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          data-testid="invite-user-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          data-testid="invite-user-submit-button"
        >
          {isSubmitting ? 'Inviting...' : 'Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
